import { NestApiType } from '@/composables/nestApi';
import type {
  SpmFilterConfig,
  SpmRelationFilterConfig,
  SpmApiFilter,
  SpmActiveSort,
  SpmActiveFilter,
} from './filters';

// Re-export filter types for convenience
export type {
  SpmFilterConfig,
  SpmRelationFilterConfig,
  SpmApiFilter,
  SpmActiveSort,
  SpmActiveFilter,
};
export { SpmFilterOperator } from './filters';
export type { SpmFilterType } from './filters';

export type OrderParam = {
  field: string;
  type: 'asc' | 'desc';
};

/**
 * @deprecated Use SpmFilterOperator from './filters' instead
 */
export type FilterOperator =
  | 'EQUALS'
  | 'NOT_EQUALS'
  | 'CONTAINS'
  | 'NOT_CONTAINS'
  | 'STARTS_WITH'
  | 'ENDS_WITH'
  | 'LESS_THAN'
  | 'LESS_THAN_OR_EQUAL_TO'
  | 'GREATER_THAN'
  | 'GREATER_THAN_OR_EQUAL_TO'
  | 'IN'
  | 'NOT_IN'
  | 'LIKE'
  | 'BETWEEN'
  | 'NOT_BETWEEN';

/**
 * @deprecated Use SpmApiFilter from './filters' instead
 */
export type FilterParam = {
  field: string;
  operator: FilterOperator;
  value?: any;
  values?: any[];
};

// Configuration for API calls
export interface ApiCallConfig {
  type_api: NestApiType;
  path: string;
  secure: boolean;
}

export interface StandardQueryListing {
  limit?: number;
  offset?: number;
  order?: OrderParam[];
  fields?: string[] | string; // Optional: if backend supports field selection
  filters?: SpmApiFilter[] | FilterParam[]; // Support both new and legacy filter types
  orFilters?: SpmApiFilter[] | FilterParam[]; // Filters joined with OR logic
}

export interface PaginationMetaDto {
  total_items: number;

  items_per_page: number;

  total_pages: number;

  current_page: number;

  offset: number;

  has_previous_page: boolean;

  has_next_page: boolean;

  next_page: number | null;

  previous_page: number | null;
}

export interface ResponseMetaDto {
  pagination: PaginationMetaDto;

  generated_at: string;
}

export interface DataResponseDto {
  statusCode: number;

  data: any;

  meta: ResponseMetaDto;
}

// Configuration for SpmTableAPI columns
/**
 * @deprecated Use SpmFilterOperator from './filters' instead
 */
export type SpmColumnFilterMatchMode =
  | 'startsWith'
  | 'contains'
  | 'notContains'
  | 'endsWith'
  | 'equals'
  | 'notEquals'
  | 'in'
  | 'lt'
  | 'lte'
  | 'gt'
  | 'gte'
  | 'between'
  | 'dateIs'
  | 'dateIsNot'
  | 'dateBefore'
  | 'dateAfter';

/**
 * @deprecated Use SpmFilterType from './filters' instead
 */
export type SpmFieldFilterType = 'text' | 'number' | 'date' | 'boolean' | 'multiSelect' | 'dropdown';

/**
 * Field configuration for sorting and filtering
 *
 * - `name`: API field name
 * - `label`: Display label for the field (optional, defaults to name)
 * - `sortable`: If true, field can be sorted
 * - `filterable`: If true, field can be filtered
 * - `filterConfig`: Filter configuration (type, options, operators)
 */
export interface SpmFieldConfig {
  name: string;
  label?: string;
  sortable?: boolean;
  filterable?: boolean;
  filterConfig?: SpmFilterConfig;
  relationFilterConfig?: SpmRelationFilterConfig;
}

/**
 * Column configuration for SpmTableAPI
 *
 * - `id`: Unique identifier for the column (used for slots: `cell-${id}`)
 * - `header`: Header text or i18n key
 * - `fields`: Array of field names (string) or field configs (SpmFieldConfig)
 * - `hidden`: If true, fields are fetched but column is not displayed (for expandable data)
 *
 * Usage:
 * - Simple field (no sort/filter):
 *   { id: 'email', header: 'Email', fields: ['contact_data_email'] }
 *
 * - Field with text filter:
 *   { id: 'email', header: 'Email', fields: [{
 *     name: 'contact_data_email',
 *     sortable: true,
 *     filterable: true,
 *     filterConfig: { type: 'textInput' }
 *   }]}
 *
 * - Field with dropdown filter:
 *   { id: 'status', header: 'Status', fields: [{
 *     name: 'status',
 *     filterable: true,
 *     filterConfig: {
 *       type: 'singleSelect',
 *       options: [{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }]
 *     }
 *   }]}
 *
 * - Composite column:
 *   { id: 'fullname', header: 'Name', fields: [
 *     { name: 'first_name', sortable: true },
 *     { name: 'last_name', sortable: true }
 *   ]}
 *
 * - Hidden (expandable):
 *   { id: 'optins', header: '', fields: ['optin_email', 'optin_sms'], hidden: true }
 */
export interface SpmColumnConfig {
  id: string;
  header: string;
  fields: (string | SpmFieldConfig)[];
  hidden?: boolean;
  /**
   * If true, this column's values are loaded after the listing via the
   * `lazyLoadFn` prop (skeleton shown until the row has been enriched).
   */
  lazy?: boolean;
  /** Optional max width for the column (e.g., '30%', '200px'). Content will wrap if exceeded. */
  maxWidth?: string;
}

/**
 * Helper function to get the field name from a field entry
 */
export function getFieldName(field: string | SpmFieldConfig): string {
  return typeof field === 'string' ? field : field.name;
}

/**
 * Helper function to get the field config from a field entry
 * Returns a normalized SpmFieldConfig object
 */
export function getFieldConfig(field: string | SpmFieldConfig): SpmFieldConfig {
  if (typeof field === 'string') {
    return { name: field };
  }
  return field;
}

/**
 * Helper function to extract all unique field names from columns
 */
export function extractApiFields(columns: SpmColumnConfig[]): string[] {
  const fieldSet = new Set<string>();
  columns.forEach((col) => {
    col.fields.forEach((f) => {
      fieldSet.add(getFieldName(f));
    });
  });
  return Array.from(fieldSet);
}

/**
 * Helper function to find field config by name across all columns
 * Returns the first matching config found (warns if duplicates with different configs)
 */
export function findFieldConfig(columns: SpmColumnConfig[], fieldName: string): SpmFieldConfig | null {
  let foundConfig: SpmFieldConfig | null = null;

  columns.forEach((col) => {
    col.fields.forEach((f) => {
      const name = getFieldName(f);
      if (name === fieldName) {
        const config = getFieldConfig(f);
        if (foundConfig === null) {
          foundConfig = config;
        } else if (typeof f !== 'string') {
          // Field found again with explicit config - use the most detailed one
          // Merge configs, preferring non-undefined values from the new config
          foundConfig = {
            ...foundConfig,
            ...Object.fromEntries(
              Object.entries(config).filter(([, v]) => v !== undefined),
            ),
          } as SpmFieldConfig;
        }
      }
    });
  });

  return foundConfig;
}

/**
 * Helper function to get all sortable fields from columns
 */
export function getSortableFields(columns: SpmColumnConfig[]): SpmFieldConfig[] {
  const sortableFields: SpmFieldConfig[] = [];
  const seenNames = new Set<string>();

  columns.forEach((col) => {
    col.fields.forEach((f) => {
      const config = getFieldConfig(f);
      if (config.sortable && !seenNames.has(config.name)) {
        seenNames.add(config.name);
        sortableFields.push(config);
      }
    });
  });

  return sortableFields;
}

/**
 * Helper function to get all filterable fields from columns
 */
export function getFilterableFields(columns: SpmColumnConfig[]): SpmFieldConfig[] {
  const filterableFields: SpmFieldConfig[] = [];
  const seenNames = new Set<string>();

  columns.forEach((col) => {
    col.fields.forEach((f) => {
      const config = getFieldConfig(f);
      if (config.filterable && !seenNames.has(config.name)) {
        seenNames.add(config.name);
        filterableFields.push(config);
      }
    });
  });

  return filterableFields;
}

// Selection info structure passed to grouped actions
export interface SelectionInfo {
  selectAllMode: boolean; // true if "select all" mode is active
  selectedRows: string[]; // Array of selected row keys (empty in selectAll mode) - supports composite keys
  excludedRows: string[]; // Array of excluded row keys (only used in selectAll mode) - supports composite keys
  totalRecords: number; // Total number of records in the datatable
  selectionCount: number; // Computed count of selected records
  filters: SpmApiFilter[]; // Active filters applied to the datatable (needed for backend when selectAllMode is true)
}

// Grouped actions configuration
export interface GroupedActionOption {
  label: string;
  icon?: string;
  command: (selectionInfo: SelectionInfo) => void;
  disabled?: boolean;
}

// Row actions configuration
export interface RowActionOption {
  label: string;
  icon?: string;
  class?: string; // CSS class for styling (e.g., 'p-button-danger')
  command: (rowData: any, rowKey?: string | string[], event?: any) => void; // Function called with the full row data, row key, and optional event
  visible?: (rowData: any) => boolean; // Function to determine if action should be visible for this row
  disabled?: (rowData: any) => boolean; // Function to determine if action should be disabled for this row
  separator?: boolean; // If true, renders a separator instead of an action
}

export interface DialogData {
  message: string;
  header: string;
  icon: string;
  acceptLabel: string;
  rejectLabel: string;
  actionType: string;
  idCustomer?: number | null;
  listName?: string | null;
  customFunction?: Function;
}

// Export configuration for SpmTableAPI
export interface ExportConfig {
  entityType: string; // Type of entity being exported (e.g., 'contacts', 'lists', 'campaigns')
  additionalParams?: Record<string, any>; // Additional parameters specific to each dataTable
}

// Payload emitted when export action is triggered
export interface ExportPayload {
  selectAllMode: boolean; // true if "Export all" or "Select all" mode, false if manual selection
  selectedIds: string[]; // IDs to export (selectedRows) or exclude (excludedRows if selectAllMode)
  filters?: SpmApiFilter[]; // Active filters (only when selectAllMode from selection, not from "Export all" button)
  totalCount: number; // Total number of items to export
  exportConfig: ExportConfig; // Config passed as prop
}

// Interface for methods exposed by SpmTableAPI component
export interface SpmTableAPIExposed {
  reloadData: () => Promise<void>;
  dt: any; // DataTable ref
  unselectAll: () => void;
  setFilter: (filter: SpmApiFilter) => void;
  removeFilter: (fieldName: string) => void;
  clearFilters: () => void;
  setSort: (sort: SpmActiveSort | null) => void;
  applyFiltersAndSort: (filters: SpmActiveFilter[], sort: SpmActiveSort | null) => void;
  resetFiltersAndSort: () => void;
  activeFilters: any; // Ref<SpmActiveFilter[]>
  activeSort: any; // Ref<SpmActiveSort | null>
  setLoading: (value: boolean) => Promise<void>;
}
